// A believable, entirely fictional agency for Studio previews ("Riverside Fire
// Rescue"). Everything is generated relative to `now`, so a preview never
// looks stale, and it is deterministic (seeded), so the same template always
// previews the same way.
//
// Shape: the fixtures object boardData.enableDemoData() takes —
//   { collections: { path: [{ id, ...data }] }, docs: { path: data },
//     fetch(url), call(name, data), radar(minutesAgo) }
// The storm picture (cells, warnings, reports) and the fake radar echoes are
// built from the same cell list, so the preview map is internally consistent.

export const DEMO_AGENCY = "Demo";
export const DEMO_STATION = { lat: 37.6872, lng: -97.3301 };
const AGENCY_NAME = "Riverside Fire Rescue";

const MIN = 60 * 1000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;
const ts = (ms) => ({ seconds: Math.floor(ms / 1000), nanoseconds: 0 });

// Small deterministic PRNG so previews don't reshuffle on every load.
function rng(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Point `distMi` from (lat, lng) along compass `bearingDeg`.
export function offset(lat, lng, bearingDeg, distMi) {
  const R = 3958.7613;
  const d = distMi / R;
  const b = (bearingDeg * Math.PI) / 180;
  const φ1 = (lat * Math.PI) / 180;
  const λ1 = (lng * Math.PI) / 180;
  const φ2 = Math.asin(Math.sin(φ1) * Math.cos(d) + Math.cos(φ1) * Math.sin(d) * Math.cos(b));
  const λ2 = λ1 + Math.atan2(Math.sin(b) * Math.sin(d) * Math.cos(φ1), Math.cos(d) - Math.sin(φ1) * Math.sin(φ2));
  return { lat: (φ2 * 180) / Math.PI, lng: (λ2 * 180) / Math.PI };
}

const at = (bearing, dist) => offset(DEMO_STATION.lat, DEMO_STATION.lng, bearing, dist);

// ---- storm ------------------------------------------------------------------

const CELL_DEFS = [
  { stormId: "K4", threat: "tornado", from: 235, dist: 24, heading: 50, mph: 36, tvs: true, meso: true, hailIn: 2, topKft: 52, maxDbz: 68, poh: 100, posh: 90 },
  { stormId: "B7", threat: "severe", from: 275, dist: 41, heading: 82, mph: 30, meso: true, hailIn: 1.75, topKft: 47, maxDbz: 63, poh: 90, posh: 70 },
  { stormId: "R2", threat: "strong", from: 322, dist: 58, heading: 70, mph: 27, hailIn: 0.75, topKft: 41, maxDbz: 57, poh: 60, posh: 20 },
  { stormId: "T9", threat: "moderate", from: 188, dist: 34, heading: 62, mph: 22, topKft: 33, maxDbz: 48, poh: 10, posh: 0 },
];

function stormCells(now) {
  return CELL_DEFS.map((c, i) => {
    const pos = at(c.from, c.dist);
    // Station relative to the storm: along-track distance and cross-track miss.
    const toStation = (c.from + 180) % 360;
    const rel = ((toStation - c.heading) * Math.PI) / 180;
    const along = c.dist * Math.cos(rel);
    const cross = Math.abs(c.dist * Math.sin(rel));
    const approaching = along > 0 && cross <= 12;
    const track = [0, 15, 30, 45, 60].map((m) => {
      const p = offset(pos.lat, pos.lng, c.heading, (c.mph * m) / 60);
      return [p.lng, p.lat];
    });
    return {
      id: `demo-cell-${i}`,
      nexrad: "ICT",
      stormId: c.stormId,
      lat: pos.lat,
      lng: pos.lng,
      distanceMi: c.dist,
      bearingFromStation: c.from,
      drct: (c.heading + 180) % 360,
      headingDeg: c.heading,
      speedKt: c.mph / 1.150779,
      speedMph: c.mph,
      maxDbz: c.maxDbz,
      topKft: c.topKft,
      vil: Math.round(c.maxDbz * 0.9),
      hailIn: c.hailIn || 0,
      poh: c.poh,
      posh: c.posh,
      tvs: !!c.tvs,
      meso: !!c.meso,
      validMs: now - 2 * MIN,
      track,
      threat: c.threat,
      approaching,
      etaMin: approaching ? (along / c.mph) * 60 : null,
      closestMi: approaching ? cross : null,
    };
  });
}

// A warning polygon along a cell's path: from a few miles behind it to
// `aheadMi` ahead, `halfWidthMi` either side.
function pathPolygon(cell, aheadMi, halfWidthMi) {
  const back = offset(cell.lat, cell.lng, cell.headingDeg + 180, 6);
  const ahead = offset(cell.lat, cell.lng, cell.headingDeg, aheadMi);
  const l = cell.headingDeg - 90;
  const r = cell.headingDeg + 90;
  const pts = [
    offset(back.lat, back.lng, l, halfWidthMi * 0.7),
    offset(ahead.lat, ahead.lng, l, halfWidthMi),
    offset(ahead.lat, ahead.lng, r, halfWidthMi),
    offset(back.lat, back.lng, r, halfWidthMi * 0.7),
  ];
  const ring = pts.map((p) => [p.lng, p.lat]);
  ring.push(ring[0]);
  return { type: "Polygon", coordinates: [ring] };
}

function stormAlerts(cells, now) {
  const [tor, svr, strong] = cells;
  return [
    {
      id: "demo-alert-tor",
      event: "Tornado Warning",
      severity: "Extreme",
      urgency: "Immediate",
      headline: "Tornado Warning issued for southwest Riverside County",
      areaDesc: "Southwest Riverside County",
      senderName: "NWS Demo Office",
      onsetMs: now - 9 * MIN,
      expiresMs: now + 36 * MIN,
      geometry: pathPolygon(tor, 22, 7),
      affectsStation: false,
    },
    {
      id: "demo-alert-svr",
      event: "Severe Thunderstorm Warning",
      severity: "Severe",
      urgency: "Immediate",
      headline: "Severe Thunderstorm Warning including Riverside",
      areaDesc: "Riverside; Central Riverside County",
      senderName: "NWS Demo Office",
      onsetMs: now - 14 * MIN,
      expiresMs: now + 51 * MIN,
      geometry: pathPolygon(svr, 50, 11),
      affectsStation: true,
    },
    {
      id: "demo-alert-sps",
      event: "Special Weather Statement",
      severity: "Moderate",
      urgency: "Expected",
      headline: "Strong thunderstorm north of Riverside",
      areaDesc: "Northern Riverside County",
      senderName: "NWS Demo Office",
      onsetMs: now - 5 * MIN,
      expiresMs: now + 40 * MIN,
      geometry: pathPolygon(strong, 25, 8),
      affectsStation: false,
    },
    {
      id: "demo-alert-watch",
      event: "Tornado Watch",
      severity: "Severe",
      urgency: "Expected",
      headline: "Tornado Watch 412 in effect until 11 PM",
      areaDesc: "Riverside County and surrounding counties",
      senderName: "Storm Prediction Center",
      onsetMs: now - 2 * HOUR,
      expiresMs: now + 4 * HOUR,
      geometry: null,
      affectsStation: true,
    },
  ];
}

const REPORT_DEFS = [
  { source: "nws", type: "Funnel Cloud", city: "Viola", from: 232, dist: 27, ago: 6 },
  { source: "mping", type: "Hail 1.00 inch", from: 229, dist: 20, ago: 9 },
  { source: "nws", type: "Hail", magnitude: 1.75, unit: "INCH", city: "Clearwater", from: 240, dist: 14, ago: 18 },
  { source: "mping", type: "Wind Damage", from: 262, dist: 22, ago: 25 },
  { source: "nws", type: "Tstm Wnd Gst", magnitude: 62, unit: "MPH", city: "Cheney", from: 270, dist: 30, ago: 41 },
  { source: "nws", type: "Flash Flood", city: "Haysville", from: 185, dist: 12, ago: 95 },
  { source: "nws", type: "Tstm Wnd Dmg", city: "Mount Hope", from: 305, dist: 44, ago: 150 },
  { source: "mping", type: "Hail 0.50 inch", from: 318, dist: 51, ago: 205 },
];

function stormReports(now) {
  return REPORT_DEFS.map((r, i) => {
    const p = at(r.from, r.dist);
    return {
      id: `demo-report-${i}`,
      source: r.source,
      type: r.type,
      magnitude: r.magnitude ?? null,
      unit: r.unit ?? null,
      city: r.city || "",
      county: "Riverside",
      state: r.city ? "KS" : "",
      remark: "",
      reporter: r.source === "mping" ? "mPING" : "Trained Spotter",
      lat: p.lat,
      lng: p.lng,
      distanceMi: r.dist,
      timeMs: now - r.ago * MIN,
    };
  });
}

export function demoStormData(now = Date.now(), { radiusMi = 75, hours = 6 } = {}) {
  const cells = stormCells(now).filter((c) => c.distanceMi <= radiusMi);
  const reports = stormReports(now).filter((r) => r.distanceMi <= radiusMi && r.timeMs >= now - hours * HOUR);
  const alerts = stormAlerts(stormCells(now), now);
  return {
    generatedAt: now,
    center: { ...DEMO_STATION },
    radiusMi,
    hours,
    cells,
    reports,
    alerts,
    sources: {
      nexrad: { ok: true, count: cells.length },
      lsr: { ok: true, count: reports.filter((r) => r.source === "nws").length },
      nws: { ok: true, count: alerts.length, partial: false },
      mping: { configured: true, ok: true, count: reports.filter((r) => r.source === "mping").length },
    },
  };
}

// Fake reflectivity for the preview map: each cell is a cluster of weighted
// points stretched along its motion (intense core, lighter trailing rain),
// placed where the cell was `minutesAgo` minutes back, plus a light stratiform
// band. The storm map draws these as a heatmap with a reflectivity ramp.
const CORE = { tornado: 0.75, severe: 0.62, strong: 0.5, moderate: 0.35 };

// Echo-only storms farther out (not tracked cells), so the regional view has
// the line of storms a severe-weather day actually looks like.
const DISTANT = [
  { from: 300, dist: 95, heading: 60, mph: 30, peak: 0.55, size: 1.4 },
  { from: 285, dist: 120, heading: 60, mph: 30, peak: 0.5, size: 1.6 },
  { from: 270, dist: 105, heading: 60, mph: 30, peak: 0.6, size: 1.3 },
  { from: 250, dist: 115, heading: 55, mph: 28, peak: 0.5, size: 1.5 },
  { from: 235, dist: 140, heading: 55, mph: 28, peak: 0.45, size: 1.8 },
  { from: 215, dist: 150, heading: 50, mph: 26, peak: 0.4, size: 1.6 },
  { from: 330, dist: 150, heading: 65, mph: 32, peak: 0.35, size: 2 },
  { from: 60, dist: 110, heading: 70, mph: 25, peak: 0.25, size: 2.2 },
];

export function demoRadarFrame(minutesAgo = 0) {
  const features = [];
  const cells = stormCells(Date.now());
  cells.forEach((c, ci) => {
    const rand = rng(1000 + ci);
    const back = (c.speedMph * minutesAgo) / 60;
    const center = offset(c.lat, c.lng, c.headingDeg + 180, back);
    const peak = CORE[c.threat] || 0.5;
    for (let i = 0; i < 55; i += 1) {
      // Mostly behind and around the core, like a real cell's precip shield.
      const along = (rand() - 0.7) * 12;
      const across = (rand() - 0.5) * (6 + Math.abs(along) * 0.5);
      const p0 = offset(center.lat, center.lng, c.headingDeg, along);
      const p = offset(p0.lat, p0.lng, c.headingDeg + 90, across);
      const d = Math.hypot(along / 6, across / 3);
      features.push({
        type: "Feature",
        geometry: { type: "Point", coordinates: [p.lng, p.lat] },
        properties: { w: Math.max(0.05, peak * Math.exp(-d * d)) },
      });
    }
  });
  DISTANT.forEach((d, di) => {
    const rand = rng(3000 + di);
    const c0 = at(d.from, d.dist);
    const center = offset(c0.lat, c0.lng, d.heading + 180, (d.mph * minutesAgo) / 60);
    for (let i = 0; i < 45; i += 1) {
      const along = (rand() - 0.6) * 16 * d.size;
      const across = (rand() - 0.5) * 8 * d.size;
      const p0 = offset(center.lat, center.lng, d.heading, along);
      const p = offset(p0.lat, p0.lng, d.heading + 90, across);
      const dd = Math.hypot(along / (7 * d.size), across / (4 * d.size));
      features.push({
        type: "Feature",
        geometry: { type: "Point", coordinates: [p.lng, p.lat] },
        properties: { w: Math.max(0.05, d.peak * Math.exp(-dd * dd)) },
      });
    }
  });
  // Stratiform rain band trailing the line of storms.
  const rand = rng(77);
  const drift = (26 * minutesAgo) / 60;
  for (let i = 0; i < 90; i += 1) {
    const s = at(250 + rand() * 60, 45 + rand() * 30);
    const p = offset(s.lat, s.lng, 250, drift);
    features.push({
      type: "Feature",
      geometry: { type: "Point", coordinates: [p.lng, p.lat] },
      properties: { w: 0.08 + rand() * 0.12 },
    });
  }
  return { type: "FeatureCollection", features };
}

// ---- agency data ------------------------------------------------------------

const PEOPLE = [
  ["Marcus", "Bell", "Captain"], ["Dana", "Ortiz", "Lieutenant"], ["Evan", "Park", "Firefighter/EMT"],
  ["Riley", "Chen", "Paramedic"], ["Jordan", "Hayes", "Engineer"], ["Casey", "Novak", "Firefighter"],
  ["Sam", "Whitaker", "EMT"], ["Alexis", "Grant", "Paramedic"], ["Morgan", "Reyes", "Firefighter/EMT"],
  ["Taylor", "Brooks", "Firefighter"], ["Jamie", "Foster", "Lieutenant"], ["Quinn", "Adler", "EMT"],
];

function users() {
  return PEOPLE.map(([first, last, rank], i) => ({
    id: `demo-user-${i}`,
    agency: DEMO_AGENCY,
    firstName: first,
    lastName: last,
    name: `${first} ${last}`,
    rank,
    OndutyFire: i < 7 && i % 3 !== 2,
    OndutyEMS: i < 7 && (i % 3 === 2 || i === 3),
  }));
}

function personnel(now) {
  const certs = ["EMT-B", "Paramedic", "Firefighter II", "Hazmat Ops", "CPR / BLS", "ACLS", "Driver/Operator", "PALS"];
  return PEOPLE.map(([first, last], i) => ({
    id: `demo-person-${i}`,
    name: `${first} ${last}`,
    certifications: [
      { name: certs[i % certs.length], expiration: ts(now + ((i * 11) % 70 - 6) * DAY) },
      { name: certs[(i + 3) % certs.length], expiration: ts(now + (200 + i * 20) * DAY) },
    ],
  }));
}

const UNITS = [
  ["Engine 1", true], ["Engine 2", true], ["Ladder 1", true], ["Rescue 1", true], ["Medic 1", true],
  ["Medic 2", false], ["Brush 1", true], ["Tanker 1", true], ["Battalion 1", true], ["Utility 1", false],
];
const unitDocs = () =>
  UNITS.map(([label, inService], i) => ({
    id: `demo-unit-${i}`,
    label,
    unitNumber: label,
    inService,
    outOfService: !inService,
    status: inService ? "In Service" : "Out of Service",
  }));

function workOrders(now) {
  return [
    ["Replace front brake pads", "Engine 2", "High", "In Progress", 1],
    ["Aerial ladder annual inspection", "Ladder 1", "High", "Open", 3],
    ["Stretcher mount rattle", "Medic 2", "Medium", "Waiting on Parts", 4],
    ["Oil change & filters", "Rescue 1", "Low", "Open", 7],
    ["Light bar intermittent", "Brush 1", "Medium", "Open", 9],
    ["Pump test", "Engine 1", "Medium", "Scheduled", 12],
  ].map(([title, unitNumber, priority, status, dueIn], i) => ({
    id: `demo-wo-${i}`,
    title,
    unitNumber,
    priority,
    status,
    dueDate: ts(now + dueIn * DAY),
  }));
}

function inventory() {
  return [
    ["Nitrile gloves (L)", 6, 20, "Supply room"], ["N95 respirators", 14, 25, "Supply room"],
    ["Trauma dressings", 40, 15, "EMS closet"], ["Saline 1L", 8, 12, "EMS closet"],
    ["Class A foam (gal)", 55, 20, "Apparatus bay"], ["AA batteries", 22, 30, "Radio room"],
    ["Oxygen D cylinders", 5, 6, "EMS closet"], ["Hose gaskets", 60, 25, "Apparatus bay"],
    ["Road flares", 18, 24, "Apparatus bay"], ["Hand sanitizer", 30, 10, "Supply room"],
    ["Cervical collars", 9, 8, "EMS closet"], ["Traffic cones", 24, 12, "Apparatus bay"],
  ].map(([name, quantity, threshold, location], i) => ({ id: `demo-inv-${i}`, name, quantity, threshold, location }));
}

function calendar(now) {
  const day = (n, h, m = 0) => {
    const d = new Date(now + n * DAY);
    d.setHours(h, m, 0, 0);
    return ts(d.getTime());
  };
  return [
    ["Truck company drill", day(0, 18, 30), "Training grounds"],
    ["EMS continuing education", day(1, 19), "Station 1 classroom"],
    ["Monthly business meeting", day(3, 19, 30), "Station 1"],
    ["Hose testing", day(5, 9), "Station 2"],
    ["Pancake breakfast fundraiser", day(8, 7), "Station 1 apparatus bay"],
    ["Live burn training", day(11, 8), "County burn tower"],
  ].map(([title, startDate, location], i) => ({ id: `demo-cal-${i}`, title, startDate, location }));
}

function memos(now) {
  return [
    ["Severe weather staffing", "All off-duty personnel: expect a possible recall tonight. Keep your pagers on and check in with the duty officer if you are available.", 1],
    ["New SCBA cylinders in service", "Engines 1 and 2 have been refit with the new 45-minute cylinders. Review the bottle-swap procedure before your next shift.", 26],
    ["Station 1 open house", "Saturday 10–2. Volunteers needed for the kids' hose line and the smoke trailer. Sign up on the board by the kitchen.", 50],
    ["Radio reprogramming", "Portables are being reprogrammed this week. Swap your radio at the officer's desk; spares are charged and labeled.", 96],
  ].map(([title, message, hoursAgo], i) => ({
    id: `demo-memo-${i}`,
    title,
    message,
    privacy: "All",
    postTime: ts(now - hoursAgo * HOUR),
  }));
}

const CALL_TYPES = [
  ["EMS", 0.46], ["Fire Alarm", 0.14], ["MVA", 0.11], ["Structure Fire", 0.05], ["Brush Fire", 0.06],
  ["Service Call", 0.1], ["Hazmat", 0.03], ["Rescue", 0.05],
];

function alarmHistory(now) {
  const rand = rng(2024);
  const out = [];
  for (let d = 1; d <= 90; d += 1) {
    const perDay = 3 + Math.floor(rand() * 6) + (d % 7 === 5 ? 3 : 0);
    for (let k = 0; k < perDay; k += 1) {
      let x = rand();
      let type = CALL_TYPES[0][0];
      for (const [t, p] of CALL_TYPES) {
        if (x < p) { type = t; break; }
        x -= p;
      }
      out.push({ id: `demo-hist-${d}-${k}`, CallType: type, TimeStamp: ts(now - d * DAY - rand() * DAY), AlarmStatus: false });
    }
  }
  return out;
}

function activeAlarms(now) {
  const a = at(210, 3.2);
  const b = at(75, 5.6);
  return [
    { id: "demo-active-1", CallType: "Structure Fire", CallSubType: "Residential — smoke showing", Address: "412 Cedar Ln", Town: "Riverside",
      Latitude: a.lat, Longitude: a.lng, AlarmStatus: true, TimeStamp: ts(now - 7 * MIN) },
    { id: "demo-active-2", CallType: "MVA w/ Injuries", CallSubType: "2 vehicles, 1 entrapment", Address: "Hwy 54 & 127th St E", Town: "Riverside",
      Latitude: b.lat, Longitude: b.lng, AlarmStatus: true, TimeStamp: ts(now - 3 * MIN) },
  ];
}

// Round badge for the header, as an inline SVG so it needs no hosting.
const LOGO =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64"><circle cx="32" cy="32" r="30" fill="#7f1d1d" stroke="#fca5a5" stroke-width="3"/><path d="M32 12c6 9 12 14 12 24a12 12 0 0 1-24 0c0-5 3-9 6-12 0 5 2 8 5 9-2-7 1-14 1-21z" fill="#fde68a"/><text x="32" y="58" font-family="Arial" font-weight="700" font-size="8" fill="#fecaca" text-anchor="middle">RFR</text></svg>`
  );

function weatherJson() {
  return {
    name: "Riverside",
    weather: [{ main: "Thunderstorm", description: "thunderstorm with rain", icon: "11d" }],
    main: { temp: 79, feels_like: 83, humidity: 71, temp_min: 74, temp_max: 88 },
    wind: { speed: 22, deg: 210 },
  };
}

export function demoFixtures(now = Date.now()) {
  const history = alarmHistory(now);
  const rig = `Rigchecks/VgTv80ik2BtavT0A5owc/${DEMO_AGENCY} Rigchecks`;
  return {
    collections: {
      [DEMO_AGENCY]: memos(now),
      users: users(),
      [rig]: unitDocs(),
      [`Agencies/${DEMO_AGENCY}/FleetTrack`]: unitDocs(),
      [`Agencies/${DEMO_AGENCY}/units`]: unitDocs(),
      [`Workorders/Agencies/${DEMO_AGENCY}`]: workOrders(now),
      [`Agencies/${DEMO_AGENCY}/Personnel`]: personnel(now),
      [`Agencies/${DEMO_AGENCY}/Inventory`]: inventory(),
      [`Agencies/${DEMO_AGENCY}/calendar`]: calendar(now),
      [`${DEMO_AGENCY} Alarms`]: [...activeAlarms(now), ...history],
      [`Agencies/${DEMO_AGENCY}/Alarms`]: [],
    },
    docs: {
      [`Agencies/${DEMO_AGENCY}/Settings/Info`]: {
        name: AGENCY_NAME,
        logo: LOGO,
        lat: DEMO_STATION.lat,
        long: DEMO_STATION.lng,
      },
    },
    fetch(url) {
      if (url.includes("openweathermap.org")) return weatherJson();
      if (url.includes("api.weather.gov/alerts")) {
        return {
          features: stormAlerts(stormCells(Date.now()), Date.now())
            .filter((a) => a.affectsStation)
            .map((a) => ({ properties: { event: a.event, areaDesc: a.areaDesc } })),
        };
      }
      return undefined;
    },
    call(name, data) {
      if (name === "getstormdata") return demoStormData(Date.now(), data || {});
      throw new Error(`No demo data for ${name}`);
    },
    radar: demoRadarFrame,
  };
}
